import { MouseEvent, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  Categoria,
  ComposerPayload,
  Demanda,
  formatarData,
  isVencida,
} from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";
import { hidratarInline, idsInlineDoHtml } from "../../utils/suporte/anexosInline";
import { sanitizarCorpo } from "../../utils/suporte/sanitize";
import { AnexoThumbs, Lightbox } from "./anexos";
import Comentarios from "./Comentarios";
import Composer from "./Composer";
import { StatusPill, UserAvatar } from "./shared";

interface CardResumoProps {
  titulo: string;
  meta: string;
  cor: string;
  icone: ReactNode;
  children: ReactNode;
}

function CardResumo({ titulo, meta, cor, icone, children }: CardResumoProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const [aberto, setAberto] = useState(false);

  return (
    <Box sx={styles.cardResumo(cor)}>
      <Box sx={styles.cardResumoTopo(cor)} onClick={() => setAberto((v) => !v)}>
        {icone}
        <Typography sx={styles.cardResumoTitulo}>{titulo}</Typography>
        <Typography sx={styles.cardResumoMeta}>{meta}</Typography>
        {aberto ? (
          <ExpandLessIcon sx={{ fontSize: 19 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 19 }} />
        )}
      </Box>
      <Collapse in={aberto} timeout="auto">
        <Box sx={styles.cardResumoCorpo}>{children}</Box>
      </Collapse>
    </Box>
  );
}

function MetaItem({
  icone,
  label,
  children,
}: {
  icone: ReactNode;
  label: string;
  children: ReactNode;
}) {
  const theme = useTheme();
  const styles = styless(theme);
  return (
    <Box sx={styles.metaItem}>
      <Box sx={styles.metaIcone}>{icone}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={styles.metaLabel}>{label}</Typography>
        <Box sx={styles.metaValor}>{children}</Box>
      </Box>
    </Box>
  );
}

function Categorias({ categorias }: { categorias: Categoria[] }) {
  const theme = useTheme();
  const styles = styless(theme);
  const visiveis = categorias.filter((c) => c.subcategorias.length > 0);
  if (!visiveis.length) return null;

  return (
    <Box>
      <Typography sx={styles.blocoLabel}>Categorias</Typography>
      <Box sx={styles.categoriasLinha}>
        {visiveis.map((categoria) =>
          categoria.subcategorias.map((sub) => (
            <Chip
              key={`${categoria.id}-${sub.id}`}
              size="small"
              label={`${categoria.nome} · ${sub.nome}`}
              sx={styles.categoriaChip}
            />
          )),
        )}
      </Box>
    </Box>
  );
}

interface DemandaDrawerProps {
  demanda: Demanda | null;
  usuarioId: string;
  somenteLeitura?: boolean;
  onClose: () => void;
  onTentarNovamente: (demandaId: string) => void;
  onAdicionarComentario: (payload: ComposerPayload) => Promise<void>;
  onResponder: (comentarioId: string, payload: ComposerPayload) => Promise<void>;
  onEditarComentario: (
    comentarioId: string,
    payload: ComposerPayload,
  ) => Promise<void>;
  onArquivar?: (demanda: Demanda) => void;
  arquivando?: boolean;
}

export default function DemandaDrawer({
  demanda,
  usuarioId,
  somenteLeitura = false,
  onClose,
  onTentarNovamente,
  onAdicionarComentario,
  onResponder,
  onEditarComentario,
  onArquivar,
  arquivando = false,
}: DemandaDrawerProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    setPreview(null);
  }, [demanda?.id]);

  const abertura = useMemo(() => {
    if (!demanda?.abertura) return null;
    const inline = new Set(idsInlineDoHtml(demanda.abertura.observacao));
    return {
      html: sanitizarCorpo(
        hidratarInline(demanda.abertura.observacao, demanda.abertura.anexos),
      ),
      naFaixa: demanda.abertura.anexos.filter((anexo) => !inline.has(anexo.id)),
    };
  }, [demanda]);

  const abrirPreview = (e: MouseEvent<HTMLDivElement>) => {
    const img = (e.target as HTMLElement).closest("img[data-anexo-id]");
    const src = img?.getAttribute("src");
    if (src) setPreview(src);
  };

  const vencida = demanda ? isVencida(demanda) : false;

  return (
    <Drawer
      anchor="right"
      open={!!demanda}
      onClose={onClose}
      PaperProps={{ sx: styles.drawerPaper }}
    >
      {demanda && (
        <Box sx={styles.drawerRoot}>
          <Box sx={styles.drawerHeader}>
            <Box sx={styles.drawerHeaderTopo}>
              <Tooltip title="Fechar" arrow>
                <IconButton onClick={onClose} size="small">
                  {mobile ? <CloseIcon /> : <KeyboardDoubleArrowLeftIcon />}
                </IconButton>
              </Tooltip>
              <Box component="span" sx={styles.codigo}>
                {demanda.codigo}
              </Box>
              {demanda.arquivada && (
                <Box
                  component="span"
                  sx={styles.selo(theme.palette.chip.arquive)}
                >
                  <Inventory2OutlinedIcon sx={{ fontSize: 13 }} />
                  Arquivado
                </Box>
              )}
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                <StatusPill status={demanda.status} />
                {onArquivar && !demanda.pendenteSync && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={arquivando}
                    sx={styles.botaoArquivarTexto}
                    startIcon={
                      arquivando ? (
                        <CircularProgress size={14} thickness={5} />
                      ) : demanda.arquivada ? (
                        <UnarchiveOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArchiveOutlinedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={() => onArquivar(demanda)}
                  >
                    {demanda.arquivada ? "Restaurar" : "Arquivar"}
                  </Button>
                )}
              </Box>
            </Box>
            <Typography sx={styles.drawerTitulo}>{demanda.titulo}</Typography>

            <Box sx={styles.metaStrip}>
              <MetaItem
                icone={<PersonOutlineIcon sx={{ fontSize: 17 }} />}
                label="Responsável"
              >
                {demanda.responsavel ? (
                  <>
                    <UserAvatar
                      nome={demanda.responsavel}
                      foto={demanda.responsavelFoto}
                      tamanho={22}
                    />
                    {demanda.responsavel}
                  </>
                ) : (
                  "A definir"
                )}
              </MetaItem>

              <MetaItem
                icone={<EventOutlinedIcon sx={{ fontSize: 17 }} />}
                label="Previsão"
              >
                <Box
                  component="span"
                  sx={vencida ? styles.dataVencida : undefined}
                >
                  {formatarData(demanda.dataPrevista)}
                </Box>
              </MetaItem>

              <MetaItem
                icone={<ScheduleOutlinedIcon sx={{ fontSize: 17 }} />}
                label="Aberto em"
              >
                {formatarData(demanda.dataCriacao)}
              </MetaItem>
            </Box>
          </Box>

          <Box sx={styles.drawerScroll}>
            {abertura && demanda.abertura && (
              <CardResumo
                titulo="Abertura"
                cor={theme.palette.info.main}
                icone={<FlagOutlinedIcon sx={{ fontSize: 16 }} />}
                meta={[
                  formatarData(demanda.abertura.data),
                  demanda.abertura.criadoPor
                    ? `por ${demanda.abertura.criadoPor}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <Box>
                  <Typography sx={styles.blocoLabel}>Descrição</Typography>
                  {abertura.html ? (
                    <Typography
                      component="div"
                      sx={styles.corpoHtml}
                      onClick={abrirPreview}
                      dangerouslySetInnerHTML={{ __html: abertura.html }}
                    />
                  ) : (
                    <Typography sx={styles.blocoVazio}>
                      Nenhuma descrição informada.
                    </Typography>
                  )}
                  {!!abertura.naFaixa.length && (
                    <Box sx={{ mt: 1.25 }}>
                      <AnexoThumbs
                        itens={abertura.naFaixa}
                        onPreview={setPreview}
                      />
                    </Box>
                  )}
                </Box>
                <Categorias categorias={demanda.abertura.categorias} />
              </CardResumo>
            )}

            {demanda.conclusao && (
              <CardResumo
                titulo="Conclusão"
                cor={theme.palette.chip.completed}
                icone={<TaskAltOutlinedIcon sx={{ fontSize: 16 }} />}
                meta={[
                  formatarData(demanda.conclusao.data),
                  demanda.conclusao.fechadoPor
                    ? `por ${demanda.conclusao.fechadoPor}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <Box>
                  <Typography sx={styles.blocoLabel}>
                    Causa do problema
                  </Typography>
                  <Typography sx={styles.corpoHtml}>
                    {demanda.conclusao.observacaoProblema}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={styles.blocoLabel}>Solução</Typography>
                  <Typography sx={styles.corpoHtml}>
                    {demanda.conclusao.observacaoSolucao}
                  </Typography>
                </Box>
                <Categorias categorias={demanda.conclusao.categorias} />
              </CardResumo>
            )}

            {demanda.pendenteSync && demanda.descartado ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                <AlertTitle>Chamado recusado</AlertTitle>
                Não será reenviado. Abra um novo chamado.
                {demanda.erroSync ? (
                  <Box component="div" sx={{ mt: 1, opacity: 0.85 }}>
                    {demanda.erroSync}
                  </Box>
                ) : null}
              </Alert>
            ) : demanda.pendenteSync ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <AlertTitle>Chamado ainda não enviado</AlertTitle>
                Enviaremos automaticamente assim que houver conexão. Não abra outro.
              </Alert>
            ) : somenteLeitura ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                <AlertTitle>Conversa indisponível por aqui</AlertTitle>
                Este chamado é atendido pelo canal antigo de suporte.
              </Alert>
            ) : demanda.erroSync ? (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => onTentarNovamente(demanda.id)}
                  >
                    Tentar novamente
                  </Button>
                }
              >
                {demanda.erroSync}
              </Alert>
            ) : !demanda.detalheCarregado ? (
              <Box sx={{ mt: 2 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={64}
                    sx={{ mb: 1.25, borderRadius: 2 }}
                  />
                ))}
              </Box>
            ) : (
              <Comentarios
                comentarios={demanda.comentarios}
                usuarioId={usuarioId}
                readOnly={demanda.concluida}
                onPreview={setPreview}
                onResponder={onResponder}
                onEditar={onEditarComentario}
              />
            )}
          </Box>

          {!demanda.concluida && !demanda.pendenteSync && !somenteLeitura && (
            <Box sx={styles.docaComposer}>
              <Composer
                key={demanda.id}
                limparAoEnviar
                placeholder="Escreva um comentário para a equipe…"
                dica="Cole prints direto no editor."
                textoBotao="Enviar"
                iconeBotao={<SendRoundedIcon fontSize="small" />}
                onPreview={setPreview}
                onEnviar={onAdicionarComentario}
              />
            </Box>
          )}
        </Box>
      )}

      <Lightbox src={preview} onClose={() => setPreview(null)} />
    </Drawer>
  );
}
